import type {
  ConversationThreadId,
  MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import DotsVerticalIcon from "@redotech/redo-icons/dots-vertical.svg";
import EyeOffFilledIcon from "@redotech/redo-icons/eye-off_filled.svg";
import EyeIcon from "@redotech/redo-icons/eye.svg";
import HeartIcon from "@redotech/redo-icons/heart.svg";
import HeartFilledIcon from "@redotech/redo-icons/heart_filled.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { memo, useState } from "react";
import { catchApiErrors } from "../../db/error-handling";

interface MetaCommentActionsProps {
  messageId: MessageId;
  threadId: ConversationThreadId;
  commentId: string;
  channel: MessageChannel;
  isHidden?: boolean;
  isLiked?: boolean;
}

export const MetaCommentActions = memo(function MetaCommentActions({
  messageId,
  threadId,
  commentId,
  channel,
  isHidden = false,
  isLiked = false,
}: MetaCommentActionsProps) {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const [loading, setLoading] = useState(false);
  const [hidden, setHidden] = useState(isHidden);
  const [liked, setLiked] = useState(isLiked);
  const [open, setOpen] = useState(false);

  const platform: "facebook" | "instagram" =
    channel === "facebookComments" ? "facebook" : "instagram";
  const isFacebook = platform === "facebook";

  const handleToggleVisibility = useHandler(async () => {
    setLoading(true);
    try {
      const client = effectApi.getClient();
      const call = client.conversation.toggleCommentVisibility({
        payload: { threadId, messageId, commentId, platform, hide: !hidden },
      });
      await effectApi.runPromise(catchApiErrors(call));
      setHidden(!hidden);
    } catch (error) {
      console.error("Failed to toggle comment visibility:", error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  });

  const handleDelete = useHandler(async () => {
    if (!confirm("Are you sure you want to delete this comment?")) {
      return;
    }
    setLoading(true);
    try {
      const client = effectApi.getClient();
      const call = client.conversation.deleteComment({
        payload: { threadId, messageId, commentId, platform },
      });
      await effectApi.runPromise(catchApiErrors(call));
    } catch (error) {
      console.error("Failed to delete comment:", error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  });

  const handleToggleLike = useHandler(async () => {
    if (!isFacebook) {
      return; // Like is only supported for Facebook
    }
    setLoading(true);
    try {
      const client = effectApi.getClient();
      if (liked) {
        const call = client.conversation.unlikeComment({
          payload: { threadId, messageId, commentId },
        });
        await effectApi.runPromise(catchApiErrors(call));
      } else {
        const call = client.conversation.likeComment({
          payload: { threadId, messageId, commentId },
        });
        await effectApi.runPromise(catchApiErrors(call));
      }
      setLiked(!liked);
    } catch (error) {
      console.error("Failed to toggle comment like:", error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  });

  return (
    <DropdownMenu onOpenChange={setOpen} open={open}>
      <DropdownMenuTrigger
        className="flex h-6 w-6 items-center justify-center rounded opacity-0 transition-opacity hover:bg-gray-100 group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <DotsVerticalIcon className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem disabled={loading} onClick={handleToggleVisibility}>
          {hidden ? (
            <>
              <EyeIcon className="mr-2 h-4 w-4" />
              <span>Unhide comment</span>
            </>
          ) : (
            <>
              <EyeOffFilledIcon className="mr-2 h-4 w-4" />
              <span>Hide comment</span>
            </>
          )}
        </DropdownMenuItem>
        {isFacebook && (
          <DropdownMenuItem disabled={loading} onClick={handleToggleLike}>
            {liked ? (
              <>
                <HeartIcon className="mr-2 h-4 w-4" />
                <span>Unlike comment</span>
              </>
            ) : (
              <>
                <HeartFilledIcon className="mr-2 h-4 w-4" />
                <span>Like comment</span>
              </>
            )}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="text-red-600"
          disabled={loading}
          onClick={handleDelete}
        >
          <TrashIcon className="mr-2 h-4 w-4" />
          <span>Delete comment</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
